import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import AdminDashboard from './components/AdminDashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import AuditLogPage from './components/AuditLogPage';
import UserDashboard from './components/UserDashboard';
import SuperAdminLogin from './components/SuperAdminLogin';
import ForcePasswordReset from './components/ForcePasswordReset';
import CompanySetupWizard from './components/CompanySetupWizard';
import api from './services/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    let isMounted = true;
    const bootstrapSession = async () => {
      const currentUser = await api.restoreSession();
      if (!isMounted) return;
      setUser(currentUser);
      setLoading(false);
    };
    bootstrapSession();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleUserUpdate = useCallback((userData) => {
    setUser(userData);
  }, []);

  const handleLogout = useCallback(() => {
    api.logout();
    setUser(null);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      handleLogout();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    };
    const syncAuthState = () => {
      setUser(api.getCurrentUser());
    };
    const handleStorage = (event) => {
      if (!['token', 'refresh_token', 'user'].includes(event.key || '')) return;
      syncAuthState();
    };
    window.addEventListener('app:auth-expired', handleAuthExpired);
    window.addEventListener('app:auth-changed', syncAuthState);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('app:auth-expired', handleAuthExpired);
      window.removeEventListener('app:auth-changed', syncAuthState);
      window.removeEventListener('storage', handleStorage);
    };
  }, [handleLogout]);

  if (loading) {
    return (
      <div className="App">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const isImpersonateRoute = window.location.pathname.startsWith('/impersonate');
  if (isImpersonateRoute) {
    const pendingToken = localStorage.getItem('impersonation_token');
    const pendingRefreshToken = localStorage.getItem('impersonation_refresh_token');
    const pendingUser = localStorage.getItem('impersonation_user');
    if (pendingToken && pendingUser) {
      let parsedUser = null;
      try {
        parsedUser = JSON.parse(pendingUser);
      } catch (_err) {
        parsedUser = null;
      }
      api.setAuthSession({
        token: pendingToken,
        refreshToken: pendingRefreshToken,
        user: parsedUser
      });
      localStorage.removeItem('impersonation_token');
      localStorage.removeItem('impersonation_refresh_token');
      localStorage.removeItem('impersonation_user');
      window.location.replace('/');
      return null;
    }
  }

  const isLoginRoute = window.location.pathname === '/login';
  const isSetupRoute = window.location.pathname.startsWith('/superadmin/setup');
  const isAuditRoute = window.location.pathname.startsWith('/superadmin/audit');
  const isSuperAdminRoute = window.location.pathname.startsWith('/superadmin');

  if (user && user.force_password_reset) {
    return <ForcePasswordReset onComplete={setUser} />;
  }

  return (
    <div className="App">
      {!isOnline && (
        <div className="offline-banner" role="status" aria-live="polite">
          System is offline. We’ll save your changes and try to sync when the connection returns.
        </div>
      )}
      {isSetupRoute ? (
        !user ? (
          <SuperAdminLogin onLogin={handleLogin} />
        ) : user.is_super_admin ? (
          <CompanySetupWizard onLogout={handleLogout} />
        ) : (
          <div className="loading">
            Access denied.
            <button type="button" onClick={handleLogout} style={{ marginLeft: 12 }}>
              Logout
            </button>
          </div>
        )
      ) : isAuditRoute ? (
        !user ? (
          <SuperAdminLogin onLogin={handleLogin} />
        ) : user.is_super_admin ? (
          <AuditLogPage onLogout={handleLogout} />
        ) : (
          <div className="loading">
            Access denied.
            <button type="button" onClick={handleLogout} style={{ marginLeft: 12 }}>
              Logout
            </button>
          </div>
        )
      ) : isSuperAdminRoute ? (
        !user ? (
          <SuperAdminLogin onLogin={handleLogin} />
        ) : user.is_super_admin ? (
          <SuperAdminDashboard user={user} onLogout={handleLogout} />
        ) : (
          <div className="loading">
            Access denied.
            <button type="button" onClick={handleLogout} style={{ marginLeft: 12 }}>
              Logout
            </button>
          </div>
        )
      ) : !user ? (
        isLoginRoute ? <Login onLogin={handleLogin} /> : <LandingPage />
      ) : user.role === 'admin' || user.role === 'owner' || user.role === 'superadmin' ? (
        user.is_super_admin ? (
          <SuperAdminDashboard user={user} onLogout={handleLogout} />
        ) : (
          <AdminDashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
        )
      ) : user.role === 'user' ? (
        <UserDashboard user={user} onLogout={handleLogout} />
      ) : (
        <AdminDashboard user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
      )}
    </div>
  );
}

export default App;
